///////////////////////////////////////////////////////////////////////////////
// Copyright (C) 2002-2025, Open Design Alliance (the "Alliance").
// All rights reserved.
//
// This software and its documentation and related materials are owned by
// the Alliance. The software may only be incorporated into application
// programs owned by members of the Alliance, subject to a signed
// Membership Agreement and Supplemental Software License Agreement with the
// Alliance. The structure and organization of this software are the valuable
// trade secrets of the Alliance and its suppliers. The software is also
// protected by copyright law and international treaty provisions. Application
// programs incorporating this software must include the following statement
// with their copyright notices:
//
//   This application incorporates Open Design Alliance software pursuant to a
//   license agreement with Open Design Alliance.
//   Open Design Alliance Copyright (C) 2002-2025 by Open Design Alliance.
//   All rights reserved.
//
// By use of this software, its documentation or related materials, you
// acknowledge and accept the above terms.
///////////////////////////////////////////////////////////////////////////////

import { CameraMode, IComponent } from "@inweb/viewer-core";
import type { Viewer } from "../Viewer";

export class CameraComponent implements IComponent {
  protected viewer: Viewer;

  constructor(viewer: Viewer) {
    this.viewer = viewer;
    this.viewer.addEventListener("databasechunk", this.geometryEnd);
    this.viewer.addEventListener("clear", this.geometryEnd);
    this.viewer.addEventListener("optionschange", this.optionsChange);
    this.viewer.addEventListener("initialize", this.optionsChange);
  }

  dispose() {
    this.viewer.removeEventListener("databasechunk", this.geometryEnd);
    this.viewer.removeEventListener("clear", this.geometryEnd);
    this.viewer.removeEventListener("optionschange", this.optionsChange);
    this.viewer.removeEventListener("initialize", this.optionsChange);
  }

  getCameraMode(view: any): CameraMode {
    return view.perspective ? "perspective" : "orthographic";
  }

  switchCameraMode(mode: CameraMode) {
    if (!mode) return;

    const activeView = this.viewer.visViewer().activeView;

    if (mode !== this.getCameraMode(activeView)) {
      activeView.perspective = !activeView.perspective;

      this.viewer.update();
      this.viewer.emitEvent({ type: "changecameramode", mode });
    }

    activeView.delete();
  }

  optionsChange = () => {
    this.switchCameraMode(this.viewer.options.cameraMode);
  };

  geometryEnd = () => {
    const activeView = this.viewer.visViewer().activeView;

    const mode = this.getCameraMode(activeView);

    this.viewer.options.cameraMode = mode;
    this.viewer.emitEvent({ type: "changecameramode", mode });

    activeView.delete();
  };
}
